/**
 * Drawing a score into React, and measuring what was drawn.
 *
 * Rendering is a pure function of the document and the options, so it is a memo and not an
 * effect: a render that happened during the last commit is reused, and a score that has not
 * changed is not drawn again. What cannot be a memo is measuring, because a box only exists
 * once the browser has laid the SVG out -- so that half is a layout effect, and it is kept
 * here next to the render rather than in the view, since the two have to agree about which
 * page they are talking about.
 *
 * The engine is the caller's. This package constructs no renderer and imports none; see
 * `render/port.ts` for why.
 */

import { useLayoutEffect, useMemo, useState } from "react";

import type { Score } from "../document/types";
import { boxesFor } from "../render/geometry";
import type { Box, BoxHost } from "../render/geometry";
import { elementsOnPage } from "../render/hit";
import { barsOnPage } from "../render/slots";
import type { NotationEngine, RenderOptions } from "../render/port";
import { RenderError, renderScore } from "../render/render";
import type { Rendering } from "../render/render";

/** A drawn score, or the reason there is not one. */
export interface RenderedScore {
  readonly rendering: Rendering | null;
  /** Why the score could not be drawn, in words fit to show. */
  readonly error: string | null;
}

/**
 * Draw a score with an engine, again whenever either changes.
 *
 * A null engine is the ordinary state of a component whose engine is still loading, not a
 * mistake, and answers with no rendering and no error.
 */
export function useRendering(
  engine: NotationEngine | null,
  score: Score,
  options: RenderOptions = {},
): RenderedScore {
  const settings = JSON.stringify(options);

  return useMemo<RenderedScore>(() => {
    if (engine === null) return { rendering: null, error: null };
    try {
      return {
        rendering: renderScore(engine, score, JSON.parse(settings) as RenderOptions),
        error: null,
      };
    } catch (raised) {
      if (raised instanceof RenderError) return { rendering: null, error: raised.message };
      throw raised;
    }
  }, [engine, score, settings]);
}

/** Nothing measured, which is what a page that has not been laid out yet answers. */
const NOTHING: ReadonlyMap<string, Box> = new Map();

/**
 * Where every drawn element of one page sits, relative to `host`.
 *
 * Measuring is deliberately not automatic beyond this: a box changes when the page is
 * re-rendered, when the window is resized and when the score is zoomed, and the first of
 * those is the only one this hook can see. A caller that lets the user resize passes a
 * `revision` that changes when it wants the page measured again.
 */
export function useBoxes(
  host: { readonly current: BoxHost | null },
  rendering: Rendering | null,
  page = 1,
  revision = 0,
): ReadonlyMap<string, Box> {
  return useMeasured(host, rendering, page, revision, elementsOnPage);
}

/**
 * Where each bar of one page sits, relative to `host`.
 *
 * Bars are measured into a map of their own rather than alongside the events, because the
 * two are used for opposite questions: a bar contains every note in it, so a single map
 * would answer "the bar" for every click and the note under the pointer would never win.
 */
export function useBarBoxes(
  host: { readonly current: BoxHost | null },
  rendering: Rendering | null,
  page = 1,
  revision = 0,
): ReadonlyMap<string, Box> {
  return useMeasured(host, rendering, page, revision, barsOnPage);
}

/** One measuring pass over whichever elements the caller named. */
function useMeasured(
  host: { readonly current: BoxHost | null },
  rendering: Rendering | null,
  page: number,
  revision: number,
  select: (rendering: Rendering, page: number) => readonly string[],
): ReadonlyMap<string, Box> {
  const [boxes, setBoxes] = useState<ReadonlyMap<string, Box>>(NOTHING);

  useLayoutEffect(() => {
    const element = host.current;
    if (element === null || rendering === null) {
      setBoxes(NOTHING);
      return;
    }
    setBoxes(boxesFor(element, select(rendering, page)));
  }, [host, rendering, page, revision, select]);

  return boxes;
}
